import { collection, query, where, getDocs, Timestamp, getDoc } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { getWordsData } from './utils';
import { pickExampleSentenceIdForWord } from './words';

const CRAM_WINDOW_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

const getCutoff = () => Timestamp.fromDate(new Date(Date.now() - CRAM_WINDOW_MS));

/**
 * Fetches all cram sentences — user_sentences where last_mistake_at is within the past 24h.
 */
export const fetchCramSentencesItems = async (userId) => {
  const cutoff = getCutoff();
  const colRef = collection(firestore, 'users', userId, 'user_sentences');
  const q = query(colRef, where('last_mistake_at', '>=', cutoff));
  const snapshot = await getDocs(q);

  if (snapshot.empty) return [];

  const sentencePromises = snapshot.docs.map(async (docSnap) => {
    const userSentenceData = { id: docSnap.id, ...docSnap.data() };
    const sentenceRef = userSentenceData.sentence_ref;
    if (!sentenceRef) return null;

    const sentenceDoc = await getDoc(sentenceRef);
    if (!sentenceDoc.exists()) return null;

    const merged = { ...userSentenceData, ...sentenceDoc.data(), id: docSnap.id };
    merged.words = await getWordsData(merged.words || []);
    return merged;
  });

  const results = await Promise.all(sentencePromises);
  return results.filter(Boolean);
};

/**
 * Fetches all cram words — user_words where last_mistake_at is within the past 24h.
 */
export const fetchCramWordsItems = async (userId, learnedSentenceIds = null) => {
  const cutoff = getCutoff();
  const colRef = collection(firestore, 'users', userId, 'user_words');
  const q = query(colRef, where('last_mistake_at', '>=', cutoff));
  const snapshot = await getDocs(q);

  if (snapshot.empty) return [];

  const wordPromises = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    word_id: docSnap.data().word_ref,
    ...docSnap.data(),
  }));

  const wordsData = await getWordsData(wordPromises);
  return Promise.all(
    wordsData.map(async (word) => {
      try {
        const id = await pickExampleSentenceIdForWord(word.id, learnedSentenceIds);
        return { ...word, exampleSentenceId: id };
      } catch {
        return { ...word, exampleSentenceId: null };
      }
    })
  );
};